import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { merge, Subject, Subscription } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

import { actions, selectors } from '@lbmx/app-state';
import { Actions, ofType } from '@ngrx/effects';
import { ConfigProvider } from 'src/app/provider/config-provider';
import { SessionService } from 'src/app/services/session/session.service';

@Component({
  selector: 'app-session-setup-component',
  templateUrl: './session-setup.component.html',
})
export class SessionSetupComponent implements OnInit, OnDestroy {
  public shouldSelectMarketplace = false;
  public shouldSelectAccount = false;

  private shouldDestroy$ = new Subject<void>();

  constructor(
    private sessionService: SessionService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private configPrv: ConfigProvider,
    private actions$: Actions,
  ) {}

  public async ngOnInit() {
    this.store.dispatch(
      actions.getMarketplaces({
        endPoints: this.configPrv.AppSetting,
      }),
    );

    const navigateToUrl = decodeURIComponent(
      this.route.snapshot.queryParams['nextUrl'] ?? '/default',
    );

    const showMarketplaceModal$ = this.store.pipe(
      select(selectors.marketplaces),
      tap(({ marketplace }) => {
        this.shouldSelectMarketplace = !marketplace;
      }),
    );

    const showAccountModal$ = this.sessionService.switchAccount$.pipe(
      tap((switchAccount) => {
        this.shouldSelectAccount = switchAccount;
      }),
    );

    const navigateWhenMarketplaceSet$ = this.store.pipe(
      filter(() => !this.shouldSelectAccount && !this.shouldSelectMarketplace),
      select(selectors.userFeature),
      filter(
        ({ marketplace, marketplaces }) =>
          !!marketplace &&
          marketplaces instanceof Array &&
          marketplaces
            ?.map((m) => m.accountKey.toString())
            .includes(marketplace),
      ),
      tap(() => {
        this.router.navigate([navigateToUrl], {
          queryParams: { nextUrl: null },
          queryParamsHandling: 'merge',
        });
      }),
    );

    const reloadWhenAccountSwitched$ = this.actions$.pipe(
      ofType(actions.setAccountSuccess),
      tap(() => {
        window.location.reload();
      }),
    );

    merge(
      showMarketplaceModal$,
      showAccountModal$,
      navigateWhenMarketplaceSet$,
      reloadWhenAccountSwitched$,
    )
      .pipe(takeUntil(this.shouldDestroy$))
      .subscribe();
  }

  public ngOnDestroy() {
    this.shouldDestroy$.next();
  }
}
